/**
 * Named compute slot CRUD for a version (DR1 B5). Slots are persisted via
 * dedicated API endpoints — not embedded in the version PATCH body.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import {
  createComputeSlot,
  deleteComputeSlot,
  listComputeSlots,
  updateComputeSlot,
} from '@/services/aiStudioAgentService';
import {
  STUDIO_COMPUTE_CLASSES,
  STUDIO_SLOT_FALLBACK_POLICIES,
} from '@/types/aiStudioAgent';
import type {
  AiStudioVersionComputeSlot,
  StudioComputeClass,
  StudioSlotFallbackPolicy,
} from '@/types/aiStudioAgent';
import { FIELD_CLASS, LABEL_CLASS } from './constants';
import { FieldLabel } from './FieldLabel';

interface ComputeSlotsEditorProps {
  versionId: string;
  disabled?: boolean;
}

interface SlotDraft {
  slotName: string;
  computeClass: StudioComputeClass;
  defaultRef: string;
  fallbackPolicy: StudioSlotFallbackPolicy;
  isRequired: boolean;
  description: string;
}

const defaultDraft = (): SlotDraft => ({
  slotName: '',
  computeClass: 'generative',
  defaultRef: '',
  fallbackPolicy: 'error',
  isRequired: true,
  description: '',
});

export const ComputeSlotsEditor: React.FC<ComputeSlotsEditorProps> = ({
  versionId,
  disabled = false,
}) => {
  const { t } = useLanguage();
  const [slots, setSlots] = useState<AiStudioVersionComputeSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<SlotDraft>(defaultDraft);
  const [saving, setSaving] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setSlots(await listComputeSlots(versionId));
    } catch {
      setErrorKey('aiStudio.definition.slotsLoadFailed');
    } finally {
      setLoading(false);
    }
  }, [versionId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const handleAdd = async () => {
    if (!draft.slotName.trim()) return;
    setSaving(true);
    setErrorKey(null);
    try {
      const created = await createComputeSlot(versionId, {
        slotName: draft.slotName.trim(),
        computeClass: draft.computeClass,
        defaultRef: draft.defaultRef.trim() || null,
        fallbackPolicy: draft.fallbackPolicy,
        isRequired: draft.isRequired,
        sortOrder: slots.length,
        description: draft.description.trim() || null,
      });
      setSlots((current) => [...current, created]);
      setDraft(defaultDraft());
    } catch {
      setErrorKey('aiStudio.definition.slotsSaveFailed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (slotId: string) => {
    setSaving(true);
    setErrorKey(null);
    try {
      await deleteComputeSlot(versionId, slotId);
      setSlots((current) => current.filter((slot) => slot.id !== slotId));
    } catch {
      setErrorKey('aiStudio.definition.slotsSaveFailed');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (slot: AiStudioVersionComputeSlot, patch: Partial<SlotDraft>) => {
    setSaving(true);
    setErrorKey(null);
    try {
      const updated = await updateComputeSlot(versionId, slot.id, {
        slotName: patch.slotName ?? slot.slotName,
        computeClass: patch.computeClass ?? slot.computeClass,
        defaultRef:
          patch.defaultRef !== undefined
            ? patch.defaultRef.trim() || null
            : slot.defaultRef,
        fallbackPolicy: patch.fallbackPolicy ?? slot.fallbackPolicy,
        isRequired: patch.isRequired ?? slot.isRequired,
        description:
          patch.description !== undefined
            ? patch.description.trim() || null
            : slot.description,
      });
      setSlots((current) => current.map((s) => (s.id === slot.id ? updated : s)));
    } catch {
      setErrorKey('aiStudio.definition.slotsSaveFailed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <p className="text-base text-[var(--color-surface-text-secondary)]">
        {t('aiStudio.definition.slotsLoading')}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <FieldLabel as="span" tooltip={t('aiStudio.definition.slotsHint')}>
        {t('aiStudio.definition.sectionSlots')}
      </FieldLabel>
      {errorKey && (
        <p role="alert" className="text-base text-[var(--color-error)]">
          {t(errorKey)}
        </p>
      )}

      {slots.length === 0 ? (
        <p className="text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.definition.slotsEmpty')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {slots.map((slot) => (
            <li
              key={slot.id}
              className="grid gap-2 rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] p-3 md:grid-cols-2 lg:grid-cols-4"
            >
              <div>
                <label className={LABEL_CLASS}>{t('aiStudio.definition.slotName')}</label>
                <input
                  className={FIELD_CLASS}
                  value={slot.slotName}
                  disabled={disabled || saving}
                  onChange={(e) => void handleUpdate(slot, { slotName: e.target.value })}
                />
              </div>
              <div>
                <label className={LABEL_CLASS}>{t('aiStudio.definition.slotClass')}</label>
                <select
                  className={FIELD_CLASS}
                  value={slot.computeClass}
                  disabled={disabled || saving}
                  onChange={(e) =>
                    void handleUpdate(slot, {
                      computeClass: e.target.value as StudioComputeClass,
                    })
                  }
                >
                  {STUDIO_COMPUTE_CLASSES.map((cls) => (
                    <option key={cls} value={cls}>
                      {t(`aiStudio.versions.computeClass.${cls}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={LABEL_CLASS}>{t('aiStudio.definition.slotRef')}</label>
                <input
                  className={FIELD_CLASS}
                  value={slot.defaultRef ?? ''}
                  disabled={disabled || saving}
                  onChange={(e) => void handleUpdate(slot, { defaultRef: e.target.value })}
                />
              </div>
              <div>
                <label className={LABEL_CLASS}>{t('aiStudio.definition.slotFallback')}</label>
                <select
                  className={FIELD_CLASS}
                  value={slot.fallbackPolicy}
                  disabled={disabled || saving}
                  onChange={(e) =>
                    void handleUpdate(slot, {
                      fallbackPolicy: e.target.value as StudioSlotFallbackPolicy,
                    })
                  }
                >
                  {STUDIO_SLOT_FALLBACK_POLICIES.map((policy) => (
                    <option key={policy} value={policy}>
                      {t(`aiStudio.definition.fallback.${policy}`)}
                    </option>
                  ))}
                </select>
              </div>
              {!disabled && (
                <div className="flex items-end">
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => void handleDelete(slot.id)}
                    className="text-base text-[var(--color-error)] hover:underline disabled:opacity-50"
                  >
                    {t('aiStudio.common.remove')}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {!disabled && (
        <div className="grid gap-2 rounded border border-dashed border-[var(--color-border)] p-3 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className={LABEL_CLASS}>{t('aiStudio.definition.slotName')}</label>
            <input
              className={FIELD_CLASS}
              value={draft.slotName}
              disabled={saving}
              onChange={(e) => setDraft((d) => ({ ...d, slotName: e.target.value }))}
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>{t('aiStudio.definition.slotClass')}</label>
            <select
              className={FIELD_CLASS}
              value={draft.computeClass}
              disabled={saving}
              onChange={(e) =>
                setDraft((d) => ({ ...d, computeClass: e.target.value as StudioComputeClass }))
              }
            >
              {STUDIO_COMPUTE_CLASSES.map((cls) => (
                <option key={cls} value={cls}>
                  {t(`aiStudio.versions.computeClass.${cls}`)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={LABEL_CLASS}>{t('aiStudio.definition.slotRef')}</label>
            <input
              className={FIELD_CLASS}
              value={draft.defaultRef}
              disabled={saving}
              onChange={(e) => setDraft((d) => ({ ...d, defaultRef: e.target.value }))}
            />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              disabled={saving || !draft.slotName.trim()}
              onClick={() => void handleAdd()}
              className="rounded-md bg-[var(--color-accent-bg)] px-3 py-2 text-base text-[var(--color-accent-text)] hover:opacity-90 disabled:opacity-50"
            >
              {t('aiStudio.definition.addSlot')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
